// Labour observations (docs/plans/phase2-maternity-mch.md §2.6). The bounds are each
// measure's own scale (dilatation 0–10 cm in half centimetres, descent in fifths 0–5,
// contractions in a 10-minute window), or a typing-error bound (fetal heart rate 1–300,
// contraction length 1–300 s, plan M7). None of them is a clinical threshold: nothing here
// flags a value as abnormal.

export const AMNIOTIC_FLUID_STATES = [
  'intact',
  'clear',
  'meconium',
  'blood_stained',
  'absent',
] as const;
export type AmnioticFluid = (typeof AMNIOTIC_FLUID_STATES)[number];

export const AMNIOTIC_FLUID_LABELS: Readonly<Record<AmnioticFluid, string>> = {
  intact: 'Membranes intact',
  clear: 'Clear',
  meconium: 'Meconium stained',
  blood_stained: 'Blood stained',
  absent: 'Absent',
};

export const MOULDING_GRADES = ['none', 'one', 'two', 'three'] as const;
export type Moulding = (typeof MOULDING_GRADES)[number];

export const MOULDING_LABELS: Readonly<Record<Moulding, string>> = {
  none: 'None',
  one: '+',
  two: '++',
  three: '+++',
};

export const LABOUR_STATUSES = ['in_labour', 'delivered', 'closed', 'entered_in_error'] as const;
export type LabourStatus = (typeof LABOUR_STATUSES)[number];

export const LABOUR_STATUS_LABELS: Readonly<Record<LabourStatus, string>> = {
  in_labour: 'In labour',
  delivered: 'Delivered',
  closed: 'Closed',
  entered_in_error: 'Entered in error',
};

/**
 * Why a labour record is closed without a delivery (plan §2.5, M22). TODO(clinical-verify)
 * M22: the list is provisional. Closing labour leaves the pregnancy open; a transfer out of
 * the pregnancy's care is closed on the pregnancy itself.
 */
export const LABOUR_CLOSE_OUTCOMES = ['false_labour', 'transferred_out', 'other'] as const;
export type LabourCloseOutcome = (typeof LABOUR_CLOSE_OUTCOMES)[number];

export const LABOUR_CLOSE_OUTCOME_LABELS: Readonly<Record<LabourCloseOutcome, string>> = {
  false_labour: 'False labour',
  transferred_out: 'Transferred out',
  other: 'Other',
};

export const FETAL_HEART_RATE_MAX = 300;
export const CONTRACTION_SECONDS_MAX = 300;
export const CONTRACTIONS_WINDOW_MAX = 10;
export const DESCENT_FIFTHS_MAX = 5;
export const CERVICAL_DILATION_MAX = 10;

export interface LabourObservationInput {
  readonly cervicalDilationCm: number | null;
  readonly descentFifths: number | null;
  readonly contractionsPer10Min: number | null;
  readonly contractionSeconds: number | null;
  readonly fetalHeartRate: number | null;
  readonly amnioticFluid: AmnioticFluid | null;
  readonly moulding: Moulding | null;
  readonly hasVitals: boolean;
  readonly note: string | null;
}

export type LabourObservationErrorCode =
  | 'labour.observation_empty'
  | 'labour.dilation_invalid'
  | 'labour.descent_invalid'
  | 'labour.contractions_invalid'
  | 'labour.fetal_heart_rate_invalid';

const wholeIn = (n: number | null, min: number, max: number) =>
  n === null || (Number.isInteger(n) && n >= min && n <= max);

export function checkLabourObservation(
  input: LabourObservationInput,
): LabourObservationErrorCode | null {
  const anything =
    input.cervicalDilationCm !== null ||
    input.descentFifths !== null ||
    input.contractionsPer10Min !== null ||
    input.contractionSeconds !== null ||
    input.fetalHeartRate !== null ||
    input.amnioticFluid !== null ||
    input.moulding !== null ||
    input.hasVitals ||
    (input.note !== null && input.note.trim() !== '');
  if (!anything) return 'labour.observation_empty';
  const dilation = input.cervicalDilationCm;
  if (
    dilation !== null &&
    (dilation < 0 || dilation > CERVICAL_DILATION_MAX || !Number.isInteger(dilation * 2))
  ) {
    return 'labour.dilation_invalid';
  }
  if (!wholeIn(input.descentFifths, 0, DESCENT_FIFTHS_MAX)) return 'labour.descent_invalid';
  if (
    !wholeIn(input.contractionsPer10Min, 0, CONTRACTIONS_WINDOW_MAX) ||
    !wholeIn(input.contractionSeconds, 1, CONTRACTION_SECONDS_MAX)
  ) {
    return 'labour.contractions_invalid';
  }
  if (!wholeIn(input.fetalHeartRate, 1, FETAL_HEART_RATE_MAX)) {
    return 'labour.fetal_heart_rate_invalid';
  }
  return null;
}
